import 'react-quill/dist/quill.snow.css';
import './ArticleEditor.css'

import { Modal, Typography, message } from 'antd';
import ReactQuill, { Quill } from 'react-quill';
import { useEffect, useState } from 'react';

import Loading from '../../components/loading/Loading';
import Navbar from '../../components/navbar';
import axiosInstance from '../../api/axiosConfig';
import { styles } from './ArticleEditor.Styles';
import useDeviceWidth from '../../hooks/useDeviceWidth';
import { utils } from '../../utils';

const { Title } = Typography;

const Size = Quill.import("attributors/style/size");
Size.whitelist = ["10px", "12px", "14px", "16px", "18px", "24px", "32px"];
Quill.register(Size, true);
function TextEditor() {
    const deviceWidth = useDeviceWidth();
    const [htmlContent, setHtmlContent] = useState('');
    const [preview, setPreview] = useState(false);
    const [wordCount, setWordCount] = useState(0);
    const [readingTime, setReadingTime] = useState(0);
    const [title, setTitle] = useState("");
    const [loading, setLoading] = useState(false);
    const [improvedContent, setImprovedContent] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [openShareModal, setOpenShareModal] = useState(false);

    // Calculate word count and reading time
    useEffect(() => {
        if (htmlContent) {
            // Remove HTML tags
            let text = htmlContent.replace(/<[^>]*>/g, " ");

            // Remove image URLs (matches http/https links)
            text = text.replace(/https?:\/\/[^\s]+/g, "");

            // Split into words and filter out empty entries
            const words = text.trim().split(/\s+/).filter(word => word !== '');

            // Set word count
            setWordCount(words.length);

            // Estimate reading time (200 words per minute)
            setReadingTime(Math.ceil(words.length / 200));
        } else {
            setWordCount(0);
            setReadingTime(0);
        }
    }, [htmlContent]);

    function removeOuterQuotes(str) {
        if (
            (str.startsWith('"') && str.endsWith('"')) ||
            (str.startsWith("'") && str.endsWith("'"))
        ) {
            return str.slice(1, -1);
        }
        return str;
    }

    const handlePublish = async () => {
        try {
            setLoading(true);
            const result = await axiosInstance.post(
                "/articles",
                {
                    title: title,
                    content: htmlContent,
                    date: new Date(),
                    author: "Jackson Lyimo",
                    imageUrl: null

                }
            );
            if (result.status === 201) {
                setLoading(false);
                setTitle("");
                setWordCount(0);
                setReadingTime(0);
                setOpenModal(false);
                setPreview(false);
                setOpenShareModal(true);
            } else {
                setLoading(false);
                message.error(result.message);
            }
        } catch (error) {
            setLoading(false);
            message.error(error.message);
        }
    }

    const handleCheckGrammar = async () => {
        try {
            if (htmlContent.length <= 0) {
                message.error("Please enter content first");
            } else {
                setLoading(true);
                const result = await axiosInstance.post(
                    "/articles/check-grammar",
                    {
                        content: htmlContent,
                    }
                );
                if (result.status === 201) {
                    setImprovedContent(removeOuterQuotes(result.data.content));
                    setLoading(false);
                    setOpenModal(true);
                } else {
                    setLoading(false);
                    message.error(result.message);
                }
            }
        } catch (error) {
            setLoading(false);
            message.error(error.message);
        }
    }

    if (loading) {
        return (<Loading message="Working on it..." />);
    }

    return (
        <>
            <Navbar />
            <div style={styles.container}>
                <div style={styles.header}>
                    {
                        preview ? <></> : <div style={styles.titleWrapper}>
                            <h1 style={styles.title}>IYK Text Editor</h1>
                        </div>
                    }

                </div>
                {preview && htmlContent.length > 0 ? (
                    <div style={styles.preview}>
                        <h1 style={styles.previewTitle}>{title || 'Untitled Post'}</h1>
                        <div
                            dangerouslySetInnerHTML={{ __html: htmlContent }}
                        />
                    </div>
                ) : (
                    <>
                        <input
                            style={styles.titleInput}
                            type="text"
                            placeholder="Enter post title..."
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                        <ReactQuill
                            theme="snow"
                            value={htmlContent}
                            onChange={setHtmlContent}
                            modules={{
                                toolbar: [
                                    [{ 'header': [1, 2, 3, false] }],
                                    [{ 'size': Size.whitelist }],
                                    ['bold', 'italic', 'underline', 'strike'],
                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                    ['link', 'image'],
                                    [{ 'align': [] }], // 🔥 This adds alignment options
                                    ['clean']
                                ]
                            }}
                            formats={["header", "size", "bold", "italic", "underline", "strike", "list", "bullet", "link", "image", "align"]}
                        />

                    </>
                )}

                <div style={{
                    ...styles.footer,
                    justifyContent: deviceWidth <= 768 ? 'flex-end' : 'space-between'
                }}>
                    {
                        deviceWidth <= 768 ?
                            <></> :
                            <div style={styles.stats}>
                                {
                                    !preview && (
                                        <div>
                                            <span>{wordCount} words</span>
                                            <span> . </span>
                                            <span>{readingTime} min read</span>
                                        </div>)
                                }
                            </div>
                    }
                    <div style={styles.actions}>
                        {
                            !preview && <button
                                style={{ ...styles.button, ...styles.secondaryButton }}
                                onClick={handleCheckGrammar}
                            >
                                Check grammar
                            </button>
                        }
                        {
                            !preview && <button
                                style={{ ...styles.button, ...styles.secondaryButton }}
                                onClick={() => {
                                    setHtmlContent('');
                                    setTitle('')
                                }}
                            >
                                Clear
                            </button>
                        }

                        <button
                            style={{ ...styles.button, ...styles.switchButton, backgroundColor: preview ? 'white' : 'black', color: !preview ? 'white' : 'black', border: preview ? '1px solid #ccc' : 'none' }}
                            onClick={() => {
                                if (!preview && htmlContent.length <= 0) {
                                    message.warning("Cannot preview an empty content!");
                                } else {
                                    setPreview(!preview);
                                }
                            }}
                        >
                            {preview ? 'Edit' : 'Preview'}
                        </button>
                        {
                            preview && <button
                                style={{ ...styles.button, ...styles.primaryButton }}
                                onClick={handlePublish}
                            >
                                Publish
                            </button>
                        }
                    </div>
                </div>

                <Modal
                    open={openModal}
                    title={<Title level={2} style={{ color: 'grey' }}>Corrected Content</Title>}
                    onCancel={() => setOpenModal(false)}
                    footer={[
                        <button
                            key="cancel"
                            onClick={() => setOpenModal(false)}
                            style={{ ...styles.button, ...styles.secondaryButton }}
                        >
                            Cancel
                        </button>,
                        <button
                            key="accept"
                            type="primary"
                            style={{ ...styles.button, ...styles.secondaryButton, marginLeft: 10, backgroundColor: 'black', color: 'white' }}
                            onClick={() => {
                                setHtmlContent(improvedContent);
                                setOpenModal(false);
                                message.success("Corrected content applied successfully!");
                            }}
                        >
                            Accept Changes
                        </button>,
                    ]}
                    styles={{
                        body: {
                            maxHeight: '60vh', // limit modal body height
                            overflowY: 'auto', // enable vertical scrolling
                            paddingRight: '16px', // optional: prevents scrollbar overlap
                        }
                    }}
                >
                    <div style={styles.preview}>
                        <h1 style={styles.previewTitle}>{title || 'Untitled Post'}</h1>
                        <div dangerouslySetInnerHTML={{ __html: improvedContent }} />
                    </div>
                </Modal>
                
                <Modal
                    open={openShareModal}
                    title={<Title level={2}>Success!</Title>}
                    onCancel={() => setOpenShareModal(false)}
                    footer={[
                        <button
                            key="cancel"
                            onClick={() => {
                                setOpenShareModal(false);
                                setHtmlContent("");
                            }}
                            style={{ ...styles.button, ...styles.secondaryButton }}
                        >
                            Cancel
                        </button>,
                        <a href={
                            `https://wa.me/?text=${encodeURIComponent(`${utils.htmlToWhatsappFriendlyText(htmlContent)}\n\nIYK-Neno\n\nwww.iykcolumbusohio.org`) }`
                        } target="_blank" rel="noopener noreferrer" onClick={() => {
                            setOpenShareModal(false);
                            setHtmlContent("");
                        }}>
                        <button
                            key="accept"
                            type="primary"
                            style={{ ...styles.button, ...styles.secondaryButton, marginLeft: 10, backgroundColor: 'black', color: 'white' }}
                        >
                            Share on WhatsApp
                        </button>
                        </a>
                    ]}
                    styles={{
                        body: {
                            maxHeight: '60vh', // limit modal body height
                            overflowY: 'auto', // enable vertical scrolling
                            paddingRight: '16px', // optional: prevents scrollbar overlap
                        }
                    }}
                >
                    <div style={styles.preview}>
                        <p style={{margin: 0, padding: 0}}>Your article has been published successfully. Would you like to share it on WhatsApp as well?</p>
                    </div>
                </Modal>
            </div>
        </>
    );
}

export default TextEditor;